<script>
    import { _ } from 'svelte-i18n'
    import { replace } from 'svelte-spa-router'
    import logins from '$svr/providers/logins.json'
    import { slide } from 'svelte/transition'
    import { global } from '$src/state.svelte.js'
    import { dedupe } from '$utils/common'
    import { getConsent } from '$utils/api.js'
    import { DEFAULT_MANAGED_LOGO, SLIDE_ANIM_MS } from '$src/constants.js'
    import Dropdown from '$lib/Dropdown.svelte'
    import VerifyEmail from '$lib/VerifyEmail.svelte'
    import LoginProvider from '$lib/LoginProvider.svelte'

    let {
        selected = $bindable(''),
        expanded = $bindable(false),
        addExpanded = $bindable(false),
        emailOTPState = $bindable(false),
        handleDropdown = () => {},
        continueWithProvider = () => {},
    } = $props()

    async function verifyEmailSuccess() {
        const res = await getConsent()
        if (res.chooseWhoManages || res.chooseManagedLogo) {
            global.data = res
            return await replace('/wizard/managed')
        }
        if (!res.merge) {
            global.data.release.emails = res.release.emails
            global.data.release.accounts = res.release.accounts
            if (global.data.release.existing_usernames) {
                global.data.release.existing_usernames =
                    res.release.existing_usernames
            }
            selected = global.data.release.emails[0]
        }
        expanded = false
        addExpanded = false
    }
</script>

<div data-test="email-container" class="space-y-1">
    <label for="email" class="text-sm font-medium">
        {$_('Verified Email Address')}
    </label>
    {#if global.data?.release?.managed?.email}
        {@const meta = global.data?.release?.managed?._meta}
        <div
            class="bg-charcoal block flex h-12 items-center overflow-hidden rounded-md bg-opacity-10 px-4 dark:bg-opacity-100"
        >
            <span class="managed-rounded-square-sm">
                <img
                    src={meta.logo || DEFAULT_MANAGED_LOGO}
                    alt={meta.domain || ''}
                    class="w-4.5 max-h-[18px] object-contain"
                />
            </span>
            <span class="ml-4">{selected}</span>
        </div>
    {:else if global.data?.release?.update_scope}
        <div class="box-shadow mt-2 space-y-2 rounded-md p-2">
            <div
                data-test="emails-container"
                class="relative flex items-center"
            >
                <div class="w-full">
                    <ul class="mt-1 flex flex-col items-start space-y-2">
                        {#each dedupe(global.data?.release?.emails) as email, index (email)}
                            <li
                                data-test="email-{index}"
                                class="flex w-full items-center"
                            >
                                <input
                                    id="email-{email}"
                                    name="email"
                                    type="radio"
                                    bind:group={selected}
                                    value={email}
                                    class="form-radio text-charcoal h-4 w-4 rounded-full dark:text-[#808080]"
                                />
                                <label
                                    for="email-{email}"
                                    class="ml-2.5 inline-flex w-full items-center truncate"
                                >
                                    <span class="truncate">
                                        {email}
                                    </span>
                                </label>
                            </li>
                        {/each}
                    </ul>
                </div>
            </div>

            <div class="relative">
                <Dropdown
                    hasBackground={false}
                    dataTest="add-provider-btn"
                    ariaLabel="Add another email"
                    expanded={addExpanded}
                    onclick={() => handleDropdown('addEmail')}
                >
                    <div
                        class="flex h-12 items-center px-4 text-left"
                        aria-hidden="true"
                    >
                        {$_('Add another email')}
                    </div>

                    {#if addExpanded}
                        <ul
                            class="p-2"
                            transition:slide={{ duration: SLIDE_ANIM_MS }}
                        >
                            <VerifyEmail
                                otpState={() => (emailOTPState = true)}
                                success={verifyEmailSuccess}
                            />
                            <span
                                class="my-3 block text-center text-lg uppercase opacity-80"
                            >
                                {$_('Or')}
                            </span>
                            <div class="space-y-2">
                                {#each logins.filter((i) => i.claims.verified_email) as provider}
                                    <LoginProvider
                                        {provider}
                                        prefix={Array.isArray(
                                            global.data?.accounts,
                                        ) &&
                                        global.data?.accounts.includes(
                                            provider.slug,
                                        )
                                            ? 'Get another email from'
                                            : 'Get email from'}
                                        onclick={(e) =>
                                            continueWithProvider(
                                                provider.slug,
                                                'email',
                                                e,
                                            )}
                                    />
                                {/each}
                            </div>
                        </ul>
                    {/if}
                </Dropdown>
            </div>
        </div>
    {:else}
        <Dropdown
            hasBackground={false}
            dataTest="email-dropdown-btn"
            id="email-dropdown"
            ariaLabel={selected}
            {expanded}
            onclick={() => handleDropdown('email')}
        >
            <div
                data-test="selected-email"
                class="flex h-12 w-full items-center justify-between px-2 no-underline"
                aria-hidden="true"
            >
                {selected}
            </div>
            {#if expanded}
                {@const emails = Array.isArray(global.data?.release?.emails)
                    ? dedupe(global.data.release.emails).filter(
                          (i) => i !== selected,
                      )
                    : []}
                <div
                    class="px-indent flex w-full flex-col text-left"
                    transition:slide={{ duration: SLIDE_ANIM_MS }}
                >
                    <ul
                        class="mt-0.5 {emails.length
                            ? 'border-y'
                            : 'border-t'} divide-y divide-[#808080] border-[#808080]"
                    >
                        {#each emails as email, index (email)}
                            <li>
                                <button
                                    data-test={`email-${index}`}
                                    onclick={() => {
                                        selected = email
                                        expanded = false
                                    }}
                                    class="flex h-12 w-full flex-shrink-0 items-center justify-start no-underline"
                                    class:opacity-60={email === selected}
                                >
                                    {email}
                                </button>
                            </li>
                        {/each}
                    </ul>

                    <div class="mt-2">
                        <VerifyEmail
                            otpState={() => (emailOTPState = true)}
                            success={verifyEmailSuccess}
                        />
                    </div>
                    {#if !emailOTPState && logins.filter((i) => i.claims.verified_email).length}
                        <span
                            class="my-4 block text-center text-lg uppercase opacity-60"
                        >
                            {$_('Or')}
                        </span>
                        <ul class="mb-2 space-y-2">
                            {#each logins.filter((i) => i.claims.verified_email) as provider}
                                <li>
                                    <LoginProvider
                                        onclick={(e) =>
                                            continueWithProvider(
                                                provider.slug,
                                                'email',
                                                e,
                                            )}
                                        {provider}
                                        prefix="Get email from"
                                    />
                                </li>
                            {/each}
                        </ul>
                    {/if}
                </div>
            {/if}
        </Dropdown>
    {/if}
</div>

<style>
    @media (prefers-color-scheme: dark) {
        .box-shadow {
            box-shadow: 0 0 0 1px #808080;
        }
    }

    @media (prefers-color-scheme: light) {
        .box-shadow {
            box-shadow: 0 0 0 1px #303030;
        }
    }

    ul li button:hover,
    ul li button:focus {
        box-shadow:
            0px -2px 0px rgb(128, 128, 128),
            0px 2px 0px rgb(128, 128, 128);
    }
</style>
